import { useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import {
  loadRivals,
  loadRivalRequests,
  challengeUser,
  acceptRivalRequest as acceptRivalRequestApi,
  declineRivalRequest as declineRivalRequestApi,
  endRivalry as endRivalryApi,
} from '../api/rivals'
import type { Rival, RivalRequest } from '../types'

export interface UseRivalsOptions {
  /** The signed-in user, so incoming/outgoing can be split. Empty string until the load lands. */
  myUserId: string
  onToast: (message: string) => void
}

export interface RivalsController {
  rivals: Rival[]
  setRivals: Dispatch<SetStateAction<Rival[]>>
  rivalRequests: RivalRequest[]
  setRivalRequests: Dispatch<SetStateAction<RivalRequest[]>>
  /** Challenges sent to you — drives the home tab's list and the bottom-nav badge count. */
  incomingRequests: RivalRequest[]
  /** Challenges you sent — a viewed player shows "Pending" rather than "Challenge". */
  outgoingRequests: RivalRequest[]
  /** The one incoming request the top-of-screen banner offers, or null once dismissed. */
  bannerRequest: RivalRequest | null
  dismissRequest: (requestId: string) => void
  challenge: (userId: string, displayName: string) => Promise<void>
  acceptRequest: (requestId: string) => Promise<void>
  declineRequest: (requestId: string) => Promise<void>
  endRivalry: (userId: string, displayName: string) => Promise<void>
}

/**
 * Rivalries and the pending-challenge inbox. Accepting is the only action
 * that changes both lists, so it is the only one that refetches both; the rest patch or refetch
 * just the list they touch.
 *
 * Dismissal is deliberately client-only and not persisted: the banner is a nudge, and the request
 * itself stays in the home tab's list until it is actually answered.
 */
export function useRivals({ myUserId, onToast }: UseRivalsOptions): RivalsController {
  const [rivals, setRivals] = useState<Rival[]>([])
  const [rivalRequests, setRivalRequests] = useState<RivalRequest[]>([])
  const [dismissedRequestIds, setDismissedRequestIds] = useState<Set<string>>(new Set())

  const incomingRequests = rivalRequests.filter((r) => r.toUserId === myUserId)
  const outgoingRequests = rivalRequests.filter((r) => r.fromUserId === myUserId)

  return {
    rivals,
    setRivals,
    rivalRequests,
    setRivalRequests,
    incomingRequests,
    outgoingRequests,
    bannerRequest: incomingRequests.find((r) => !dismissedRequestIds.has(r.id)) ?? null,
    dismissRequest: (requestId) =>
      setDismissedRequestIds((prev) => new Set(prev).add(requestId)),

    challenge: async (userId, displayName) => {
      try {
        await challengeUser(userId)
        setRivalRequests(await loadRivalRequests())
        onToast(`Challenge sent to ${displayName}.`)
      } catch {
        onToast('Failed to send challenge. Please try again.')
      }
    },

    acceptRequest: async (requestId) => {
      try {
        await acceptRivalRequestApi(requestId)
        const [rivalsData, requestsData] = await Promise.all([
          loadRivals(),
          loadRivalRequests(),
        ])
        setRivals(rivalsData)
        setRivalRequests(requestsData)
        onToast('Rivalry accepted!')
      } catch {
        onToast('Failed to accept the challenge. Please try again.')
      }
    },

    declineRequest: async (requestId) => {
      try {
        await declineRivalRequestApi(requestId)
        setRivalRequests(await loadRivalRequests())
      } catch {
        onToast('Failed to decline the challenge. Please try again.')
      }
    },

    endRivalry: async (userId, displayName) => {
      try {
        await endRivalryApi(userId)
        setRivals((prev) => prev.filter((r) => r.userId !== userId))
        onToast(`Ended your rivalry with ${displayName}.`)
      } catch {
        onToast('Failed to end the rivalry. Please try again.')
      }
    },
  }
}
